
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * BlockedIPEventLogGetParams struct
 */
class BlockedIPEventLogGetParams {
  /**
   * Initialize struct
   * @param {!Date=} fromInclusive
   * @param {!Date=} toExclusive
   * @constructor
   */
  constructor (fromInclusive = new Date(0), toExclusive = new Date(0)) {
    this.FromInclusive = fromInclusive
    this.ToExclusive = toExclusive
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BlockedIPEventLogGetParams}
   * @param {!BlockedIPEventLogGetParams} other Other struct
   * @returns {!BlockedIPEventLogGetParams} This struct
   */
  copy (other) {
    if (other.FromInclusive != null) {
      if (other.FromInclusive instanceof Date) {
        this.FromInclusive = new Date(other.FromInclusive.getTime())
      } else {
        this.FromInclusive = new Date(Math.round(other.FromInclusive / 1000000))
      }
    } else {
      this.FromInclusive = null
    }
    if (other.ToExclusive != null) {
      if (other.ToExclusive instanceof Date) {
        this.ToExclusive = new Date(other.ToExclusive.getTime())
      } else {
        this.ToExclusive = new Date(Math.round(other.ToExclusive / 1000000))
      }
    } else {
      this.ToExclusive = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BlockedIPEventLogGetParams}
   * @returns {!BlockedIPEventLogGetParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BlockedIPEventLogGetParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BlockedIPEventLogGetParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BlockedIPEventLogGetParams}
   * @param {!BlockedIPEventLogGetParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BlockedIPEventLogGetParams)) {
      throw new TypeError('Instance of BlockedIPEventLogGetParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BlockedIPEventLogGetParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      FromInclusive: ((this.FromInclusive != null) ? (this.FromInclusive.getTime() * 1000000) : null),
      ToExclusive: ((this.ToExclusive != null) ? (this.ToExclusive.getTime() * 1000000) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BlockedIPEventLogGetParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BlockedIPEventLogGetParams} other Object value
   * @returns {!BlockedIPEventLogGetParams} Created struct
   */
  static fromObject (other) {
    return new BlockedIPEventLogGetParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BlockedIPEventLogGetParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BlockedIPEventLogGetParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BlockedIPEventLogGetParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3041
  }
}

export { BlockedIPEventLogGetParams };

/**
 * Fast Binary Encoding BlockedIPEventLogGetParams field model
 */
class FieldModelBlockedIPEventLogGetParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.FromInclusive = new fbe.FieldModelTimestamp(buffer, 4 + 4)
    this.ToExclusive = new fbe.FieldModelTimestamp(buffer, this.FromInclusive.fbeOffset + this.FromInclusive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.FromInclusive.fbeSize + this.ToExclusive.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.FromInclusive.fbeExtra + this.ToExclusive.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBlockedIPEventLogGetParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3041
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FromInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ToExclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!BlockedIPEventLogGetParams} fbeValue Default value, defaults is new BlockedIPEventLogGetParams()
   * @returns {!BlockedIPEventLogGetParams} BlockedIPEventLogGetParams value
   */
  get (fbeValue = new BlockedIPEventLogGetParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!BlockedIPEventLogGetParams} fbeValue BlockedIPEventLogGetParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) <= fbeStructSize) {
      fbeValue.FromInclusive = this.FromInclusive.get()
    } else {
      fbeValue.FromInclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) <= fbeStructSize) {
      fbeValue.ToExclusive = this.ToExclusive.get()
    } else {
      fbeValue.ToExclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {!BlockedIPEventLogGetParams} fbeValue BlockedIPEventLogGetParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBlockedIPEventLogGetParams}
   * @param {BlockedIPEventLogGetParams} fbeValue BlockedIPEventLogGetParams value
   */
  setFields (fbeValue) {
    this.FromInclusive.set(fbeValue.FromInclusive)
    this.ToExclusive.set(fbeValue.ToExclusive)
  }
}

export { FieldModelBlockedIPEventLogGetParams };

/**
 * Fast Binary Encoding BlockedIPEventLogGetParams model
 */
class BlockedIPEventLogGetParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBlockedIPEventLogGetParams(this.buffer, 4)
  }

  /**
   * Get the BlockedIPEventLogGetParams model
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!FieldModelBlockedIPEventLogGetParams} model BlockedIPEventLogGetParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BlockedIPEventLogGetParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBlockedIPEventLogGetParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BlockedIPEventLogGetParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BlockedIPEventLogGetParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BlockedIPEventLogGetParamsModel}
   * @param {!BlockedIPEventLogGetParams} value BlockedIPEventLogGetParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BlockedIPEventLogGetParamsModel}
   * @param {!BlockedIPEventLogGetParams} value BlockedIPEventLogGetParams value, defaults is new BlockedIPEventLogGetParams()
   * @return {!object} Deserialized BlockedIPEventLogGetParams value and its size
   */
  deserialize (value = new BlockedIPEventLogGetParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BlockedIPEventLogGetParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BlockedIPEventLogGetParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BlockedIPEventLogGetParamsModel}
   * @param {!number} prev Previous BlockedIPEventLogGetParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BlockedIPEventLogGetParamsModel };
